import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Radio,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { OrganizationAvatar } from "@basecomponents/OrganizationAvatar";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import { useGetUserTenants, useSwitchTenant } from "../hooks/useUserTenants";

export type TenantSwitchDialogProps = Readonly<{
  open: boolean;
  onClose: () => void;
}>;

export function TenantSwitchDialog({ open, onClose }: TenantSwitchDialogProps) {
  const { data, isLoading } = useGetUserTenants();
  const switchTenant = useSwitchTenant();
  const [selectedTenantId, setSelectedTenantId] = useState<
    string | undefined
  >();
  const [error, setError] = useState<string | undefined>();

  const currentTenantId = data?.currentTenantId;

  useEffect(() => {
    if (open) {
      setSelectedTenantId(currentTenantId);
      setError(undefined);
    }
  }, [open, currentTenantId]);

  const tenants = data?.tenants ?? [];
  const hasChanged = !!selectedTenantId && selectedTenantId !== currentTenantId;

  const handleSwitch = () => {
    if (!selectedTenantId) {
      return;
    }

    setError(undefined);
    switchTenant.mutate(selectedTenantId, {
      // A tenant change invalidates the claims the whole app is rendered from,
      // so reload rather than trying to refresh each dependent query.
      onSuccess: () => {
        window.location.href = window.location.origin;
      },
      onError: () => setError("We could not switch tenants. Please try again."),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" component="span">
            Switch Tenant
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose a tenant for general access.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          size="small"
          {...telemetryProps({
            name: "tenant_switch_close",
            section: "side_nav",
            label: "Close",
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={1} role="radiogroup" aria-label="Tenants">
            {tenants.map((tenant) => {
              const tenantId = tenant.tenantId;
              const isSelected = tenantId === selectedTenantId;
              const isCurrent = tenantId === currentTenantId;

              return (
                <Box
                  key={tenantId}
                  onClick={() => setSelectedTenantId(tenantId)}
                  sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    cursor: "pointer",
                    borderRadius: 1,
                    border: 1,
                    borderColor: isSelected ? "primary.main" : "divider",
                    backgroundColor: isSelected
                      ? theme.palette.action.selected
                      : "transparent",
                  })}
                >
                  <Radio
                    checked={isSelected}
                    value={tenantId}
                    inputProps={{ "aria-label": tenant.name }}
                  />
                  <OrganizationAvatar />
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    color={isSelected ? "primary.main" : "text.primary"}
                    sx={{ minWidth: 0, flex: "auto" }}
                  >
                    {tenant.name}
                  </Typography>
                  <Chip label="Tenant" size="small" />
                  {isCurrent && (
                    <Chip label="Current" size="small" variant="outlined" />
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          color="inherit"
          {...telemetryProps({
            name: "tenant_switch_cancel",
            section: "side_nav",
            label: "Cancel",
          })}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSwitch}
          disabled={!hasChanged || switchTenant.isPending}
          {...telemetryProps({
            name: "tenant_switch_confirm",
            section: "side_nav",
            label: "Switch Tenant",
          })}
        >
          Switch Tenant
        </Button>
      </DialogActions>
    </Dialog>
  );
}
